import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, Navigate, Outlet, useLocation, useOutletContext, useParams, useSearchParams } from 'react-router';
import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import type { ShopQueueItem, ShopToday } from '@popcart/core/v1';
import { api } from '../api';
import { Shell, type NavGroup } from '../ui/Shell';
import { useAuth } from '../auth/AuthProvider';
import { usePrefs } from '../ui/prefs';
import { useToast } from '../ui/toast';
import { playNewOrderTone } from '../ui/sound';
import { Icon } from '../ui/Icon';
import { canOpenShop, isPlatformAdmin, roleLabel, shopIdsFor, shopPath } from '../lib/roles.js';
import { ShopRefGate } from '../lib/shopRef';
import { detectNewOrders, lineCounts, sortQueue } from '../lib/queue.js';

// Partner Hub polling: the queue every 5 s (also in the background, so a tablet with the screen
// on another tab still chimes), the rest every 30 s.
export const QUEUE_MS = 5_000;
export const SLOW_MS = 30_000;

export const shopKeys = {
  today: (id: string) => ['shop', id, 'today'] as const,
  queue: (id: string) => ['shop', id, 'queue'] as const,
  orders: (id: string, status: string) => ['shop', id, 'orders', status] as const,
  stock: (id: string, low: boolean) => ['shop', id, 'stock', low ? 'low' : 'all'] as const,
};

export interface ShopCtx {
  /** The shop's id, for API calls. */
  shopId: string;
  /** Its short name, and the Partner Hub address built from it (/shop/<slug>): use `base` for links. */
  slug: string;
  base: string;
  today: ShopToday | undefined;
  todayQuery: UseQueryResult<ShopToday>;
  queue: ShopQueueItem[];
  queueQuery: UseQueryResult<ShopQueueItem[]>;
  /** shopOrderIds that arrived since this screen opened (for the visual flash). */
  newIds: Set<string>;
}

export function useShop(): ShopCtx {
  return useOutletContext<ShopCtx>();
}

const LINES_LABEL = { fresh: 'Fresh', grill: 'Grill', mart: 'Grocery' } as const;

/** /shop/:shopRef: the ref is the short name (or an old id / retired name, replaced in the bar). */
export function ShopLayout() {
  const { shopRef = '' } = useParams();
  return (
    <ShopRefGate shopRef={shopRef} prefix="/shop">
      {(shop) => <ShopLayoutFor key={shop.id} shopId={shop.id} slug={shop.slug} />}
    </ShopRefGate>
  );
}

function ShopLayoutFor({ shopId, slug }: { shopId: string; slug: string }) {
  const { me } = useAuth();
  const { muted } = usePrefs();
  const toast = useToast();
  const loc = useLocation();
  const [params] = useSearchParams();

  const todayQuery = useQuery({ queryKey: shopKeys.today(shopId), queryFn: () => api.shopToday(shopId), refetchInterval: SLOW_MS, enabled: !!shopId });
  const queueQuery = useQuery({
    queryKey: shopKeys.queue(shopId),
    queryFn: () => api.shopQueue(shopId),
    refetchInterval: QUEUE_MS,
    refetchIntervalInBackground: true,
    enabled: !!shopId,
  });
  const low = useQuery({ queryKey: shopKeys.stock(shopId, true), queryFn: () => api.shopStock(shopId, true), refetchInterval: SLOW_MS, enabled: !!shopId });

  // New-order alert: sound (unless muted) + toast in the live region + ticket flash.
  const seen = useRef<Set<string> | null>(null);
  const [newIds, setNewIds] = useState<Set<string>>(() => new Set());
  const mutedRef = useRef(muted);
  mutedRef.current = muted;
  useEffect(() => {
    seen.current = null;
    setNewIds(new Set());
  }, [shopId]);
  useEffect(() => {
    if (!queueQuery.data) return;
    const { fresh, seen: next } = detectNewOrders(seen.current, queueQuery.data);
    seen.current = next;
    if (!fresh.length) return;
    if (!mutedRef.current) playNewOrderTone();
    const first = fresh[0];
    toast(
      fresh.length === 1 && first ? `New order ${first.code}${first.speed === 'express' ? ' · Express' : ''}` : `${fresh.length} new orders`,
      'order',
    );
    setNewIds((s) => {
      const n = new Set(s);
      for (const f of fresh) n.add(f.shopOrderId);
      return n;
    });
  }, [queueQuery.data, toast]);

  const queue = useMemo(() => sortQueue(queueQuery.data ?? []), [queueQuery.data]);
  const open = queue.filter((q) => q.status === 'placed' || q.status === 'accepted' || q.status === 'preparing');
  const counts = lineCounts(open);
  const newCount = queue.filter((q) => q.status === 'placed').length;

  // Tab title shows waiting new orders, so a background tab still tells you.
  useEffect(() => {
    const base = document.title.replace(/^\(\d+\) /, '');
    document.title = newCount ? `(${newCount}) ${base}` : base;
  });

  if (!me || !canOpenShop(me, shopId)) return <Navigate to="/" replace />;

  const shop = todayQuery.data?.shop;
  const base = shopPath(slug, shopId);
  const line = params.get('line');
  const onToday = loc.pathname === base || loc.pathname === `${base}/`;
  const lowCount = low.data?.length ?? todayQuery.data?.kpis.lowStock ?? 0;

  const groups: NavGroup[] = [
    {
      items: [
        { to: base, label: 'Today', icon: 'home', end: true, active: onToday && !line },
        { to: `${base}/orders`, label: 'Orders', icon: 'receipt', count: open.length || null },
      ],
    },
    {
      scope: 'Stations',
      items: [
        // Phase 7: one card per order across every station, so the rider gets the whole order.
        { to: `${base}/handoff`, label: 'Ready for rider', icon: 'swap' },
        { to: `${base}/fresh`, label: 'Fresh · weigh', icon: 'scale', count: counts.fresh || null },
        { to: `${base}/grill`, label: 'Grill · kitchen', icon: 'flame', count: counts.grill || null },
        { to: `${base}/mart`, label: 'Grocery · pick', icon: 'list', count: counts.mart || null },
      ],
    },
    {
      scope: 'Shop',
      items: [
        { to: `${base}/products`, label: 'Products', icon: 'bag', count: lowCount ? `${lowCount} low` : null },
        { to: `${base}/hours`, label: 'Hours', icon: 'clock' },
        { to: `${base}/reviews`, label: 'Reviews', icon: 'star' },
        { to: `${base}/sales`, label: 'Sales', icon: 'chart' },
        { to: `${base}/staff`, label: 'Staff', icon: 'person' },
      ],
    },
  ];

  const switchable = isPlatformAdmin(me) || shopIdsFor(me).length > 1;
  const ctx: ShopCtx = { shopId, slug, base, today: todayQuery.data, todayQuery, queue, queueQuery, newIds };

  return (
    <Shell
      label="Partner Hub"
      brand="Partner Hub"
      groups={groups}
      roleText={roleLabel(me, shopId)}
      showMute
      header={
        <div className="shopcard">
          <div className="t-label">{shop?.name ?? 'Your shop'}</div>
          <div className="t-monocap">
            {shop ? [shop.kind === 'first_party' ? 'First-party' : 'Partner', ...shop.lines.map((l) => LINES_LABEL[l])].join(' · ') : ' '}
          </div>
        </div>
      }
      extraMenu={
        switchable ? (
          <>
            <div className="menu-sep" />
            {isPlatformAdmin(me) ? (
              <Link className="menu-item" to="/ops" style={{ color: 'inherit', textDecoration: 'none' }}>
                <Icon name="home" />
                Back to Ops
              </Link>
            ) : null}
            {shopIdsFor(me).length > 1 ? (
              <Link className="menu-item" to="/shops" style={{ color: 'inherit', textDecoration: 'none' }}>
                <Icon name="swap" />
                Switch shop
              </Link>
            ) : null}
          </>
        ) : null
      }
    >
      <Outlet context={ctx} />
    </Shell>
  );
}
